import queryString from 'query-string';
import each from 'lodash/each';
import noop from 'lodash/noop';
import get from 'lodash/get';
import EventEmitter from 'events';
import fetch from 'isomorphic-fetch';
import { t } from '../modules/infra/i18n';
import config from '../config';
import { showNotification } from './common/helpers/notifications';
import { CONFIRMATION_REQUIRED_STATUS } from './main/constants';
import { persistentKVStore } from '../modules/infra/persistence';

class Http {
  static faked = [];

  static getUrl(url) {
    return `${config.url}/v1${url}`;
  }

  static getDDSessionId() {
    let sessionId;

    const ddCookie = document.cookie.split('; ').find((row) => row.startsWith('_dd_s='));

    if (ddCookie) {
      const cookieParams = new URLSearchParams(ddCookie.split('=')[1]);
      sessionId = cookieParams.get('id') || undefined;
    }

    if (!sessionId) {
      sessionId = window.DD_RUM?.getInternalContext()?.session_id;
    }

    return sessionId;
  }

  static headers() {
    const headers = {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      'x-datadog-session-id': this.getDDSessionId(),
    };

    const token = persistentKVStore.getItem('token');
    if (token) {
      headers.Authorization = `JWT ${token}`;
    }

    return headers;
  }

  static rawHeaders() {
    const headers = {};
    const token = persistentKVStore.getItem('token');
    if (token) {
      headers.Authorization = `JWT ${token}`;
      headers['x-datadog-session-id'] = this.getDDSessionId();
    }

    return headers;
  }

  static rawResponseHeaders() {
    const headers = {
      'Content-Type': 'application/json',
      'x-datadog-session-id': this.getDDSessionId(),
    };

    const token = persistentKVStore.getItem('token');
    if (token) {
      headers.Authorization = `JWT ${token}`;
    }

    return headers;
  }

  static handleResponseError(status, res, notify = false, url, statusText) {
    switch (status) {
      case 401: {
        persistentKVStore.removeItem('token');
        persistentKVStore.removeItem('adminToken');
        return Promise.reject({ error: true, message: res.message || t('common:http.error.unauthorized') });
      }

      case 422: {
        let { message } = res;
        let rawMessage = message;
        try {
          rawMessage = JSON.parse(res.message);

          const nestedMessage = rawMessage[0].message;
          if (nestedMessage) {
            message = nestedMessage;

            const prefix = `"${rawMessage[0].key}"`;
            if (!nestedMessage.startsWith(prefix)) {
              message = `${prefix} ${nestedMessage}`;
            }
          }
        } catch (err) { /* not a JSON string */ }

        let errors = [];
        // Handle json schema errors
        if (Array.isArray(res.errors) && res.errors.length && res.errors[0].field !== undefined) {
          errors = res.errors.map(err => `${err.fieldName} ${err.message}`);
        }

        const errorInfo = { error: true, message, errors, rawMessage, status };
        const shouldNotify = typeof notify === 'function' ? notify(errorInfo) : notify;

        if (shouldNotify) {
          showNotification({
            title: t('common:http.error.title'),
            message,
            children: errors,
          });
        }

        return Promise.reject(errorInfo);
      }

      case CONFIRMATION_REQUIRED_STATUS: {
        return Promise.reject({
          error: true,
          status,
          ...res,
        });
      }

      default: {
        const errorInfo = {
          error: true,
          status,
          url,
          ...res,
        };

        const shouldNotify = typeof notify === 'function' ? notify(errorInfo) : notify;

        if (shouldNotify) {
          showNotification({
            title: t('common:http.error.title'),
            message: get(res, 'message', statusText),
            autoDismiss: 8,
          });
        }

        return Promise.reject(errorInfo);
      }
    }
  }

  static handleFakedRequest(fakedEndpoint, method, url, data) {
    EventEmitter.prototype.emit('fetch-start');

    return new Promise((resolve) => {
      setTimeout(() => {
        const response = fakedEndpoint.buildResponse(method, url, data);
        resolve(response);
        EventEmitter.prototype.emit('fetch-end');
        console.log('Mocked API call:', {
          method,
          url,
          data,
          response,
        });
      }, fakedEndpoint.delay || 0);
    });
  }

  static async respond(response, notify = true) {
    EventEmitter.prototype.emit('fetch-end');
    let json;
    try {
      json = await response.json();
    } catch (error) {
      return json;
    }

    if (response.ok) {
      return json;
    }

    return this.handleResponseError(response.status, json, notify, response.url);
  }

  static respondRaw(response, options = {
    returnHeaders: false,
    notify: false,
  }) {
    EventEmitter.prototype.emit('fetch-end');
    if (response.ok) {
      if (options.returnHeaders) {
        return response.blob().then(data => ({
          data,
          headers: response.headers,
        }));
      }
      return response.blob();
    }

    return response
        .json()
        // make sure we don't fail here even if error came with non-json body
        .catch(noop)
        .then(res => this.handleResponseError(response.status, res, options.notify, response.url, response.statusText));
  }

  static endRequest(error) {
    EventEmitter.prototype.emit('fetch-end');
    throw error;
  }

  static fake(fakeConfig) {
    this.faked.push(fakeConfig);
  }

  static get(url, data = {}, notify = true, headers = {}, options = {}) {
    const fakeConfig = this.lookupFakeConfig('GET', url, data);
    if (fakeConfig) {
      return this.handleFakedRequest(fakeConfig, 'GET', url, data);
    }

    EventEmitter.prototype.emit('fetch-start');
    let fullUrl = this.getUrl(url);
    const params = queryString.stringify(data, { arrayFormat: 'bracket' });
    if (params) {
      fullUrl += `?${params}`;
    }
    return fetch(fullUrl, {
      method: 'GET',
      headers: {
        ...Http.headers(),
        ...headers,
      },
      ...options,
    }).then(
      response => Http.respond(response, notify),
      Http.endRequest
    );
  }

  static getRaw(url, data = {}, notify = false, headers = {}) {
    const fakeConfig = this.lookupFakeConfig('GET_RAW', url, data);
    if (fakeConfig) {
      return this.handleFakedRequest(fakeConfig, 'GET_RAW', url, data);
    }

    EventEmitter.prototype.emit('fetch-start');
    let fullUrl = this.getUrl(url);
    const params = queryString.stringify(data, { arrayFormat: 'bracket' });
    if (params) {
      fullUrl += `?${params}`;
    }
    return fetch(fullUrl, {
      method: 'GET',
      headers: {
        ...Http.rawHeaders(),
        ...headers,
      },
    }).then(
      response => Http.respondRaw(response, { notify }),
      Http.endRequest
    );
  }

  static post(url, data = {}, notify = true, headers = {}) {
    const fakeConfig = this.lookupFakeConfig('POST', url, data);
    if (fakeConfig) {
      return this.handleFakedRequest(fakeConfig, 'POST', url, data);
    }

    EventEmitter.prototype.emit('fetch-start');
    return fetch(this.getUrl(url), {
      method: 'POST',
      headers: {
        ...Http.headers(),
        ...headers,
      },
      body: JSON.stringify(data),
    })
    .then(response => Http.respond(response, notify))
    .catch(Http.endRequest);
  }

  static postRaw(url, data = {}) {
    EventEmitter.prototype.emit('fetch-start');
    const formData = new FormData();

    each(data, (item, name) => {
      formData.append(name, item);
    });

    return fetch(this.getUrl(url), {
      method: 'POST',
      headers: Http.rawHeaders(),
      body: formData,
    }).then(
      response => Http.respond(response),
      Http.endRequest,
    );
  }

  static postWithRawResponse(url, data = {}, options = {
    returnHeaders: false,
  }) {
    EventEmitter.prototype.emit('fetch-start');
    return fetch(
      this.getUrl(url),
      {
        method: 'POST',
        headers: Http.rawResponseHeaders(),
        body: JSON.stringify(data),
      }
    ).then(
      response => Http.respondRaw(response, options),
      Http.endRequest
    );
  }

  static put(url, data = {}, notify = true, headers = {}) {
    const fakeConfig = this.lookupFakeConfig('PUT', url, data);
    if (fakeConfig) {
      return this.handleFakedRequest(fakeConfig, 'PUT', url, data);
    }

    EventEmitter.prototype.emit('fetch-start');
    return fetch(this.getUrl(url), {
      method: 'PUT',
      headers: {
        ...Http.headers(),
        ...headers,
      },
      body: JSON.stringify(data),
    })
    .then(
      response => Http.respond(response, notify),
      Http.endRequest,
    );
  }

  static putRaw(url, data = {}) {
    EventEmitter.prototype.emit('fetch-start');
    const formData = new FormData();

    each(data, (item, name) => {
      formData.append(name, item);
    });

    return fetch(this.getUrl(url), {
      method: 'PUT',
      headers: Http.rawHeaders(),
      body: formData,
    }).then(
      response => Http.respond(response),
      Http.endRequest,
    );
  }

  static delete(url, data = {}) {
    const fakeConfig = this.lookupFakeConfig('DELETE', url, data);
    if (fakeConfig) {
      return this.handleFakedRequest(fakeConfig, 'DELETE', url, data);
    }

    EventEmitter.prototype.emit('fetch-start');
    return fetch(this.getUrl(url), {
      method: 'DELETE',
      headers: Http.headers(),
      body: JSON.stringify(data),
    }).then(
      response => Http.respond(response),
      Http.endRequest
    );
  }

  static patch(url, data = {}, notify = true, headers = {}) {
    EventEmitter.prototype.emit('fetch-start');
    return fetch(this.getUrl(url), {
      method: 'PATCH',
      headers: {
        ...Http.headers(),
        ...headers,
      },
      body: JSON.stringify(data),
    })
      .then(response => Http.respond(response, notify))
      .catch(Http.endRequest);
  }

  static lookupFakeConfig(method, url, data) {
    const fakedEndpoint = this.faked.find((item) => {
      if (method !== item.method) {
        return false;
      }

      if (item.url) {
        return item.url === url;
      }

      if (item.urlRegex) {
        return item.urlRegex.test(url);
      }

      if (item.testUrl) {
        return item.testUrl(url, data);
      }

      return item.urlBuilder(data) === url;
    });

    return fakedEndpoint || null;
  }
}

export default Http;
